import { inject } from "@angular/core";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import type { SymbolTableView } from "@bitwarden/web-vault/app/models/types/symbol.types";
import { SignalSubscriptionImpl } from "../data-subscription";
import { ReferenceDataSource } from "@bitwarden/web-vault/app/models/view/reference-data/reference-data.view";
import { type ISymbolView } from "@bitwarden/web-vault/app/models/view/symbol/symbol.view";

export type TableSource = SymbolTableView;

/**
 * Get the primary source of a symbol for sorting purposes
 * Priority: FOREX > other sources (like EODHD) > MANUAL
 */
function getSymbolSourcePriority(symbol: ISymbolView): number {
  const sources = symbol.sources;

  if (!sources) {
    return 3; // Default to lowest priority
  }

  // Check if it has FOREX source
  if (sources[ReferenceDataSource.FOREX]) {
    return 0;
  }

  // Check if it has MANUAL source
  if (sources[ReferenceDataSource.MANUAL]) {
    return 2;
  }

  // Other sources (like EODHD from data banks)
  if (Object.keys(sources).length > 0) {
    return 1;
  }

  return 3; // No sources
}

/**
 * Compare function to sort symbols by source type and then alphabetically
 */
function compareSymbols(a: ISymbolView, b: ISymbolView): number {
  const aPriority = getSymbolSourcePriority(a);
  const bPriority = getSymbolSourcePriority(b);

  // First, sort by source priority
  if (aPriority !== bPriority) {
    return aPriority - bPriority;
  }

  // Within the same source type, sort alphabetically by code
  return a.code.localeCompare(b.code);
}

/**
 * This class only need to deal with angular dependency
 * and the core logic is in getAccountTableViews function which can be easily unit tested
 */
export class SymbolTableSubscription extends SignalSubscriptionImpl<TableSource[]> {
  private transactionStore = inject(TransactionStoreService);

  get source() {
    const symbols = this.transactionStore.symbols.collection();
    // SymbolsStoreCollection seeds/clears _collection to null, so `!symbols` here means "hasn't
    // loaded yet", not "loaded and empty".
    if (!symbols) {
      return null;
    }

    // Sort symbols before creating table data.
    const sortedSymbols = [...symbols].sort(compareSymbols);

    const tableData: SymbolTableView[] = [];

    for (const symbol of sortedSymbols) {
      if (symbol?.c_asOf) {
        tableData.push({
          symbol: symbol,
          rate: {
            rate: symbol.c_asOf.rate,
            cross: symbol.code,
          },
          rightChevron: true,
        });
      } else {
        tableData.push({
          symbol: symbol,
          rate: {
            rate: 1,
            cross: symbol.code,
          },
          rightChevron: false,
        });
      }
    }
    return tableData;
  }
}
